import isEmpty from 'lodash/isEmpty';
import * as yup from 'yup';

import { logToNewRelic } from '../../services/debug';
import { getErrorMessage } from '../../utils/errors';
import { isOfferPopupType } from '../../utils/events';
import { getImageUrlWithBucketPrefix } from '../../utils/images';
import { devError } from '../../utils/log';

import { BENEFIT_TYPES } from './constants';
import { EVENT_TRIGGER_LAYOUT_VERSIONS, type EventTriggerPopupData } from './types';

const layoutElementSchema = yup.object().shape({
  left: yup.number().required(),
  top: yup.number().required(),
  width: yup.number().required(),
  height: yup.number().required(),
  style: yup.object().shape({
    fontSize: yup.number().required(),
    color: yup.string().optional(),
    background: yup.string().optional(),
    display: yup.string().optional(),
  }),
});

// Define schemas for BUTTON, TIMER, CLOSE, REWARD
const actionBtnSchema = yup.object().shape({
  ...layoutElementSchema.fields,
  text: yup.string(),
  type: yup.string().required('Type ACTION_BTN is required').oneOf(['ACTION_BTN'], 'Type must be "ACTION_BTN"'),
  btnType: yup.mixed().oneOf(['base', 'main', 'light', 'orange', 'blue']),
});

const timerSchema = yup.object().shape({
  ...layoutElementSchema.fields,
  type: yup.string().required('Type TIMER is required').oneOf(['TIMER'], 'Type must be "TIMER"'),
});

const closeSchema = yup.object().shape({
  ...layoutElementSchema.fields,
  type: yup.string().required('Type CLOSE is required').oneOf(['CLOSE'], 'Type must be "CLOSE"'),
});

const rewardSchema = yup.object().shape({
  ...layoutElementSchema.fields,
  type: yup.string().required('Type REWARD is required').oneOf(['REWARD'], 'Type must be "REWARD"'),
});

// Define the schema for OFFER
const offerSchemaV1 = yup.object({
  price: yup.number().positive().required('price is required'),
  imageUrl: yup.string().required('imageUrl is required'),
  miniImageUrl: yup.string().required('miniImageUrl is required'),
  scCoin: yup.number().positive().required('scCoin is required'),
  gcCoin: yup.number().positive().required('gcCoin is required'),
  layout: yup
    .object({
      ACTION_BTN: actionBtnSchema,
      TIMER: timerSchema,
      CLOSE: closeSchema,
      REWARD: yup.object().optional().nullable(),
    })
    .required('Offer layout is required'),
});

const offerWithPackagesSchemaV1 = yup.object({
  imageUrl: yup.string().required('imageUrl is required'),
  miniImageUrl: yup.string().required('miniImageUrl is required'),
  packages: yup
    .array()
    .of(
      yup.object().shape({
        packageId: yup.number().required('packageId is required'),
        price: yup.number().required('price is required'),
        scCoin: yup.number().required('scCoin is required'),
        gcCoin: yup.number().required('gcCoin is required'),
      }),
    )
    .required(),
  layout: yup.array().required('Offer layout is required'),
});

const offerSchemaV2 = yup.object({
  price: yup.number().positive().required('price is required'),
  imageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const baseSchema = yup.string();
    return hasStoreVisibility ? baseSchema.optional().nullable() : baseSchema.required('imageUrl is required');
  }),
  miniImageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const baseSchema = yup.string();
    return hasStoreVisibility ? baseSchema.optional().nullable() : baseSchema.required('miniImageUrl is required');
  }),
  scCoin: yup.number().positive().required('scCoin is required'),
  gcCoin: yup.number().positive().required('gcCoin is required'),
  visibility: yup
    .object({
      lobby: yup.boolean(),
      store: yup.boolean(),
    })
    .optional(),
  layout: yup.lazy((value, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;

    const layoutSchema = yup.object({
      elements: yup
        .object({
          ACTION_BTN: actionBtnSchema,
          TIMER: timerSchema,
          CLOSE: closeSchema,
          REWARD: rewardSchema,
        })
        .required('elements is required'),
      attributes: yup
        .object({
          imageWidth: yup.number().required('imageWidth is required'),
          imageHeight: yup.number().required('imageHeight is required'),
        })
        .required('attributes is required'),
      version: yup.string().required('version is required'),
    });

    return hasStoreVisibility ? layoutSchema.optional() : layoutSchema.required('Offer layout is required');
  }),
  storeLayoutImageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const baseSchema = yup.string();
    return hasStoreVisibility
      ? baseSchema.required('storeLayoutImageUrl is required')
      : baseSchema.optional().nullable();
  }),
  storeLayout: yup.lazy((value, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    // yup 1.x quirk: `.optional().nullable()` on an object with nested `.required()`
    // fields still cascades when the value is undefined. Short-circuit here so a
    // missing storeLayout doesn't trip nested required errors.
    if (!hasStoreVisibility && value == null) return yup.mixed().optional();

    const storeLayoutSchema = yup.object({
      elements: yup
        .object({
          ACTION_BTN: actionBtnSchema,
          TIMER: timerSchema,
          CLOSE: closeSchema,
          REWARD: rewardSchema,
        })
        .required('elements is required'),
      attributes: yup
        .object({
          imageWidth: yup.number().required('imageWidth is required'),
          imageHeight: yup.number().required('imageHeight is required'),
        })
        .required('attributes is required'),
      version: hasStoreVisibility ? yup.string().required('version is required') : yup.string().optional().nullable(),
    });

    return hasStoreVisibility
      ? storeLayoutSchema.required('storeLayout is required')
      : storeLayoutSchema.optional().nullable();
  }),
});

const offerWithPackagesSchemaV2 = yup.object({
  visibility: yup
    .object({
      lobby: yup.boolean(),
      store: yup.boolean(),
    })
    .optional(),
  imageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const baseSchema = yup.string();
    return hasStoreVisibility ? baseSchema.optional().nullable() : baseSchema.required('imageUrl is required');
  }),
  miniImageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const isTakeOrLose = context.parent?.isTakeOrLose === true;
    const baseSchema = yup.string();
    return hasStoreVisibility || isTakeOrLose
      ? baseSchema.optional().nullable()
      : baseSchema.required('miniImageUrl is required');
  }),
  packages: yup
    .array()
    .of(
      yup.object().shape({
        packageId: yup.number().required('packageId is required'),
        price: yup.number().optional(),
        scCoin: yup.number().required('scCoin is required'),
        gcCoin: yup.number().required('gcCoin is required'),
      }),
    )
    .required(),
  layout: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;

    const layoutSchema = yup.object({
      elements: yup.array().required('elements is required'),
      attributes: yup
        .object({
          imageWidth: yup.number().required('imageWidth is required'),
          imageHeight: yup.number().required('imageHeight is required'),
        })
        .required('attributes is required'),
      version: yup.string().required('version is required'),
    });

    return hasStoreVisibility ? layoutSchema.optional() : layoutSchema.required('Offer layout is required');
  }),
  storeLayoutImageUrl: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;
    const baseSchema = yup.string();
    return hasStoreVisibility
      ? baseSchema.required('storeLayoutImageUrl is required')
      : baseSchema.optional().nullable();
  }),
  storeLayout: yup.lazy((_, context) => {
    const hasStoreVisibility = context.parent?.visibility?.store === true;

    if (hasStoreVisibility) {
      const storeLayoutSchema = yup.object({
        elements: yup.array().required('elements is required'),
        attributes: yup
          .object({
            imageWidth: yup.number().required('imageWidth is required'),
            imageHeight: yup.number().required('imageHeight is required'),
          })
          .required('attributes is required'),
        version: yup.string().required('version is required'),
      });
      return storeLayoutSchema.required('storeLayout is required');
    }

    const optionalStoreLayoutSchema = yup.object({
      elements: yup.array().optional().nullable(),
      attributes: yup
        .object({
          imageWidth: yup.number().optional().nullable(),
          imageHeight: yup.number().optional().nullable(),
        })
        .optional()
        .nullable(),
      version: yup.string().optional().nullable(),
    });
    return optionalStoreLayoutSchema.optional().nullable();
  }),
});

// Define the schema for Gift
const giftSchemaV1 = yup.object({
  imageUrl: yup.string().required(),
  scCoin: yup.number().required('scCoin is required').min(0),
  gcCoin: yup.number().required('gcCoin is required').min(0),
  layout: yup
    .object({
      ACTION_BTN: actionBtnSchema,
      REWARD: yup.object().optional().nullable(),
      FRB_REWARD: yup.object().optional().nullable(),
    })
    .test('reward-or-frb', 'Gift layout must have REWARD or FRB_REWARD', (layout) => {
      return !isEmpty(layout?.REWARD) || !isEmpty(layout?.FRB_REWARD);
    })
    .required('Gift layout is required'),
});

const giftSchemaV2 = yup.object({
  imageUrl: yup.string().required(),
  scCoin: yup.number().required('scCoin is required').min(0),
  gcCoin: yup.number().required('gcCoin is required').min(0),
  layout: yup
    .object({
      elements: yup.lazy((value) => {
        if (Array.isArray(value)) {
          return yup.array().required('elements is required');
        }

        return yup
          .object({
            ACTION_BTN: actionBtnSchema,
            REWARD: yup.object().optional().nullable(),
            FRB_REWARD: yup.object().optional().nullable(),
          })
          .test('reward-or-frb', 'Gift layout elements must have REWARD or FRB_REWARD', (elements) => {
            return !isEmpty(elements?.REWARD) || !isEmpty(elements?.FRB_REWARD);
          })
          .required('elements is required');
      }),
      attributes: yup
        .object({
          imageWidth: yup.number(),
          imageHeight: yup.number(),
        })
        .optional()
        .nullable(),
      version: yup.string().optional().nullable(),
    })
    .required('Gift layout is required'),
});

const messageSchemaV2 = yup.object({
  imageUrl: yup.string().required(),
  layout: yup
    .object({
      elements: yup
        .object({
          ACTION_BTN: actionBtnSchema,
          CLOSE: closeSchema,
        })
        .required('elements is required'),
      attributes: yup
        .object({
          imageWidth: yup.number().required('imageWidth is required'),
          imageHeight: yup.number().required('imageHeight is required'),
        })
        .required('attributes is required'),
      version: yup.string().required('version is required'),
    })
    .required('Message layout is required'),
});

const questSchemaV2 = yup.object({
  completedAt: yup
    .mixed()
    .nullable()
    .test('is-nullable', 'Field must be nullable', (value) => !value),
  layout: yup
    .object({
      elements: yup
        .object({
          ACTION_BTN: actionBtnSchema,
          CLOSE: closeSchema,
          // REWARD: rewardSchema.optional().nullable(), TODO: FIX THIS, reward should be optional
          DYNAMIC_IMAGE_1: yup.object().shape({
            ...layoutElementSchema.fields,
          }),
        })
        .required('elements is required'),
      attributes: yup
        .object({
          imageWidth: yup.number().required('imageWidth is required'),
          imageHeight: yup.number().required('imageHeight is required'),
        })
        .required('attributes is required'),
      version: yup.string().required('version is required'),
    })
    .required('Message layout is required'),
  completedAllMilestonesAt: yup.date().nullable(),
  rewardPackage: yup.object({
    packageId: yup.number(),
    scCoin: yup.number(),
    gcCoin: yup.number(),
  }),
  userQuest: yup
    .object({
      userQuestMilestones: yup.array().min(1).required('userQuestMilestones is required'),
    })
    .required('userQuest is required'),
});

const messageSchemaV1 = yup.object({
  imageUrl: yup.string().required('imageUrl is required'),
  layout: yup
    .object({
      ACTION_BTN: actionBtnSchema,
      CLOSE: closeSchema,
    })
    .required('Message layout is required'),
});

const actionSchemaV1 = yup.object({});

const actionSchemaV2 = yup.object({});

const wheelOfFortuneSchema = yup.object({
  slices: yup.array().of(yup.mixed()).required('slices is required'),
  winnerSlice: yup.number().integer().min(0).required('winnerSlice is required'),
  price: yup.number().positive().required('price is required'),
});

const roadSchemaV2 = yup.object({
  id: yup.string().uuid().required('id is required'),
  key: yup.string().required('key is required'),
  name: yup.string().required('name is required'),
  description: yup.string().optional(),
  assets: yup
    .array()
    .of(
      yup.object().shape({
        key: yup.string().required('key is required'),
        url: yup.string().required('url is required'),
        language: yup.string().required('language is required'),
      }),
    )
    .required('assets is required')
    .min(1, 'at least one asset is required'),
  addedById: yup.number().integer().required('addedById is required'),
  createdAt: yup.string().optional(),
  updatedAt: yup.string().optional(),
  lanes: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().uuid().required('lane id is required'),
        order: yup.number().integer().required('lane order is required'),
        label: yup.string().required('lane label is required'),
        paid: yup.boolean().required('lane paid is required'),
        backgroundImageUrl: yup.string().url().required('lane backgroundImageUrl is required'),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              id: yup.string().uuid().required('step id is required'),
              order: yup.number().integer().required('step order is required'),
              delay: yup.number().required('step delay is required'),
              packageImageUrl: yup.string().nullable().optional(),
              backgroundImageUrl: yup.string().url().required('step backgroundImageUrl is required'),
              package: yup
                .object()
                .shape({
                  price: yup.number().required('step package price is required'),
                  gcCoin: yup.number().required('step package gcCoin is required'),
                  scCoin: yup.number().required('step package scCoin is required'),
                  bundleItems: yup.array().nullable(),
                  packageId: yup.number().required('step package packageId is required'),
                })
                .required('step package is required'),
            }),
          )
          .required('lane steps is required'),
        package: yup
          .object()
          .shape({
            price: yup.number().required('lane package price is required'),
            gcCoin: yup.number().required('lane package gcCoin is required'),
            scCoin: yup.number().required('lane package scCoin is required'),
            bundleItems: yup.array().nullable(),
            packageId: yup.number().required('lane package packageId is required'),
          })
          .nullable()
          .optional(),
      }),
    )
    .required('lanes is required')
    .min(1, 'at least one lane is required'),
});

const benefitSchemaV1 = yup
  .object({
    benefitType: yup.mixed().oneOf(BENEFIT_TYPES).required('benefitType is required'),
    key: yup.string().required('key is required'),
  })
  .test('benefitType', (data) => {
    switch (data.benefitType) {
      case '2-3-offer':
        return Boolean(offerWithPackagesSchemaV1.validateSync(data));
      case 'offer':
        return Boolean(offerSchemaV1.validateSync(data));
      case 'wheelOfFortune':
        return Boolean(wheelOfFortuneSchema.validateSync(data));
      case 'gift':
        return Boolean(giftSchemaV1.validateSync(data));
      case 'message':
        return Boolean(messageSchemaV1.validateSync(data));
      case 'action':
        return Boolean(actionSchemaV1.validateSync(data));
      default:
        return false;
    }
  });

const benefitSchemaV2 = yup
  .object({
    benefitType: yup.mixed().oneOf(BENEFIT_TYPES).required('benefitType is required'),
    key: yup.string().required('key is required'),
  })
  .test('benefitType', (data) => {
    switch (data.benefitType) {
      case '2-3-offer':
        return Boolean(offerWithPackagesSchemaV2.validateSync(data));
      case 'rollingOffer':
        return Boolean(offerWithPackagesSchemaV2.validateSync(data));
      case 'offer':
        return Boolean(offerSchemaV2.validateSync(data));
      case 'gift':
        return Boolean(giftSchemaV2.validateSync(data));
      case 'message':
        return Boolean(messageSchemaV2.validateSync(data));
      case 'quest':
        return Boolean(questSchemaV2.validateSync(data));
      case 'road':
        return Boolean(roadSchemaV2.validateSync(data));
      case 'action':
        return Boolean(actionSchemaV2.validateSync(data));
      default:
        return false;
    }
  });

export const eventTriggerSchemaValidationV1 = yup.object().shape({
  eventTrigger: yup
    .object({
      eventTriggerId: yup.string().required('eventTriggerId is required'),
    })
    .required('eventTriggerSchemaValidation eventTrigger is required'),
  benefit: benefitSchemaV1.required('eventTriggerSchemaValidation benefit is required'),
});

export const eventTriggerSchemaValidationV2 = yup.object().shape({
  eventTrigger: yup
    .object({
      eventTriggerId: yup.string().required('eventTriggerId is required'),
    })
    .required('eventTriggerSchemaValidation eventTrigger is required'),
  benefit: benefitSchemaV2.required('eventTriggerSchemaValidation benefit is required'),
});

export function reduceInvalidEventsData(
  object: any[],
  version: string = EVENT_TRIGGER_LAYOUT_VERSIONS.V1,
): EventTriggerPopupData[] {
  const { validEvents, errors } = object.reduce(
    (acc: { validEvents: EventTriggerPopupData[]; errors: { errorMessage: string; event: any }[] }, event: any) => {
      try {
        const schemaIsValid =
          version === EVENT_TRIGGER_LAYOUT_VERSIONS.V1
            ? eventTriggerSchemaValidationV1.validateSync(event)
            : eventTriggerSchemaValidationV2.validateSync(event);
        const isOffer = isOfferPopupType(event.benefit);

        if (schemaIsValid) {
          if (isOffer) {
            event.benefit.imageUrl = getImageUrlWithBucketPrefix(event.benefit.imageUrl);
            event.benefit.miniImageUrl = getImageUrlWithBucketPrefix(event.benefit.miniImageUrl);
          }
          acc.validEvents.push(event);
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e);
        acc.errors.push({ errorMessage, event });
      }
      return acc;
    },
    { validEvents: [], errors: [] },
  );
  if (version === EVENT_TRIGGER_LAYOUT_VERSIONS.V1 && !isEmpty(errors)) {
    const invalidEvents = object.filter((event) => !validEvents.includes(event));
    return [...validEvents, ...reduceInvalidEventsData(invalidEvents, EVENT_TRIGGER_LAYOUT_VERSIONS.V2)];
  }

  if (!isEmpty(errors)) {
    devError('InvalidEventTriggerSchemaData', errors);
    logToNewRelic('InvalidEventTriggerSchemaData', JSON.stringify(errors), 'error');
  }

  return validEvents;
}

const personalGiftSchema = yup.object().shape({
  userGift: yup
    .object()
    .shape({
      id: yup.string().required('id is required'),
    })
    .required('userGift is required'),
  benefit: yup
    .object()
    .shape({
      benefitType: yup.string().required('benefitType is required'),
      scCoin: yup.number().required('scCoin is required').min(0),
      gcCoin: yup.number().required('gcCoin is required').min(0),
      layout: yup
        .object()
        .shape({
          elements: yup
            .object()
            .shape({
              ACTION_BTN: actionBtnSchema,
              REWARD: rewardSchema,
            })
            .required('elements is required'),
          attributes: yup
            .object()
            .shape({
              imageWidth: yup.number().required('imageWidth is required'),
              imageHeight: yup.number().required('imageHeight is required'),
            })
            .required('attributes is required'),
          version: yup.string().required('version is required'),
        })
        .required('layout is required'),
      imageUrl: yup.string().required('imageUrl is required'),
    })
    .required('benefit is required'),
});

export function reduceInvalidPersonalGifts(personalGifts: any[]) {
  const { validPersonalGifts, errors } = personalGifts.reduce(
    (acc: { validPersonalGifts: any[]; errors: { errorMessage: string; personalGift: any }[] }, personalGift: any) => {
      try {
        const schemaIsValid = personalGiftSchema.validateSync(personalGift);
        if (schemaIsValid) {
          acc.validPersonalGifts.push(personalGift);
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e);
        acc.errors.push({ errorMessage, personalGift });
      }
      return acc;
    },
    { validPersonalGifts: [], errors: [] },
  );
  if (!isEmpty(errors)) {
    devError('InvalidPersonalGiftSchemaData', errors);
    logToNewRelic('InvalidPersonalGiftSchemaData', JSON.stringify(errors), 'error');
  }
  return validPersonalGifts;
}
